import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon, MailIcon } from 'lucide-react';
type DonateByMailModalProps = {
  isOpen: boolean;
  onClose: () => void;
};
const EASE_OUT = [0.23, 1, 0.32, 1] as const;
export function DonateByMailModal({
  isOpen,
  onClose
}: DonateByMailModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus();
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-93" data-mp="1jvpzy3f50w5g">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-id="element-94" data-mp="1osq4g9wc9qug">
          <motion.div className="absolute inset-0 bg-black/60" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE_OUT
      }} aria-hidden="true" data-id="element-95" data-mp="1sul65fi6m68r" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="donate-by-mail-title" className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 sm:p-8 text-left max-h-[90vh] overflow-y-auto" initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.22,
        ease: EASE_OUT
      }} data-id="element-96" data-mp="27j7ntnva7a35">
            <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Close" className="absolute top-4 right-4 p-2 rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-israel-blue" data-id="element-97" data-mp="1kiqwrgd5uy2c">
              <XIcon className="w-5 h-5" data-id="element-98" data-mp="23gtdl7vk1op2" />
            </button>

            <div className="w-12 h-12 rounded-full bg-israel-blue/10 flex items-center justify-center mb-5" data-id="element-99" data-mp="23gjmfgyrxbhs">
              <MailIcon className="w-6 h-6 text-israel-blue" data-id="element-100" data-mp="2i7sj2a13fx4f" />
            </div>
            <h2 id="donate-by-mail-title" className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" data-id="element-101" data-mp="2d74vxzfzk0fh">
              Donate by Mail
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-102" data-mp="2m8m5eyyw13v7">
              Thank you for supporting the mission of the International Civilian
              Training Fund.
            </p>

            <dl className="space-y-5" data-id="element-103" data-mp="3ar4r7bwnuowk">
              <div data-id="element-104" data-mp="3aq3buv8m3g91">
                <dt className="text-sm font-medium text-gray-500 mb-1" data-id="element-105" data-mp="2wsol9n6mh2fh">
                  Please make checks payable to
                </dt>
                <dd className="text-lg font-semibold text-gray-900" data-id="element-106" data-mp="30soxr2a5v3jw">
                  International Civilian Training Fund
                </dd>
              </div>
              <div className="border-t border-gray-100 pt-5" data-id="element-107" data-mp="2rt1yam0zs7th">
                <dt className="text-sm font-medium text-gray-500 mb-1" data-id="element-108" data-mp="2rvemvi199gfi">
                  Mail to
                </dt>
                <dd className="text-lg text-gray-900 leading-relaxed" data-id="element-109" data-mp="2i3ibj5a5rttc">
                  <address className="not-italic" data-id="element-110" data-mp="2i3s1wtz482zk">
                    International Civilian Training Fund
                    <br data-id="element-111" data-mp="245u1kshmq732" />
                    P.O. Box 221615
                    <br data-id="element-112" data-mp="291slo3vxd2xo" />
                    West Palm Beach, FL 33422
                  </address>
                </dd>
              </div>
              <div className="border-t border-gray-100 pt-5" data-id="element-113" data-mp="2148q78gbpawz">
                <dt className="text-sm font-medium text-gray-500 mb-1" data-id="element-114" data-mp="1w4cnrdn9xrjj">
                  Legal name
                </dt>
                <dd className="text-gray-700" data-id="element-115" data-mp="1rai8cmepbyq9">
                  Israeli Civilian Training Fund, Inc. (DBA International
                  Civilian Training Fund)
                </dd>
              </div>
            </dl>

            <p className="mt-6 pt-5 border-t border-gray-100 text-gray-600" data-id="element-116" data-mp="1i9aau6k0f1x8">
              Questions?{' '}
              <a href="mailto:info@ictfinternational.org" className="text-israel-blue font-medium hover:underline" data-id="element-117" data-mp="18dh0mttrjkim">
                info@ictfinternational.org
              </a>
            </p>
          </motion.div>
        </div>}
    </AnimatePresence>;
}